import { type ReactNode } from 'react'
import { type UserProfileData } from '@terros/common'
import { useDisclosure } from '@mantine/hooks'
import { AppShell } from '@mantine/core'
import { useInitializeI18n, useProfile } from '@front/model'
import { useIsMobileNav } from './useIsMobileNav'
import { useSidebarNavigationContext } from './SidebarNavigationContext'
import { ShellHeaderBar } from './ShellHeaderBar'
import { NavBar } from './NavBar'
import { MobileNavBackdrop } from './MobileNavBackdrop'
import { Header, useHeaderHeight } from './Header'

export const ActiveShell = ({
  children,
}: Readonly<{
  children: ReactNode
}>): ReactNode => {
  const [burgerOpen, { toggle: toggleBurger }] = useDisclosure()
  const [isOpen] = useSidebarNavigationContext()

  const { user, refetch } = useProfile()
  useInitializeI18n()
  const headerHeight = useHeaderHeight()

  return (
    <AppShell
      header={{ height: headerHeight }}
      padding={0}
      navbar={{
        width: isOpen ? { base: 240, md: 272 } : 80,
        breakpoint: 'sm',
        collapsed: { mobile: !burgerOpen },
      }}
    >
      <PageContents burgerOpen={burgerOpen} refetch={refetch} toggleBurger={toggleBurger} user={user}>
        {children}
      </PageContents>
    </AppShell>
  )
}

type PageContentsProps = {
  children: ReactNode
  toggleBurger: VoidFunction
  burgerOpen: boolean
  refetch: VoidFunction
  user: UserProfileData | undefined
}

const PageContents = ({ children, toggleBurger, burgerOpen, refetch, user }: PageContentsProps): ReactNode => {
  const isMobileNav = useIsMobileNav()

  return (
    <>
      <Header user={user} refetch={refetch}>
        <ShellHeaderBar burgerOpen={burgerOpen} toggleBurger={toggleBurger} />
      </Header>
      {burgerOpen && isMobileNav && <MobileNavBackdrop onClose={toggleBurger} />}
      <NavBar user={user} toggleBurger={toggleBurger} isBurgerOpen={burgerOpen} />
      <AppShell.Main className='min-h-0 overflow-hidden'>{children}</AppShell.Main>
    </>
  )
}
